import { useState } from 'react';
import { AlertCircle, Lightbulb, X, Upload, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api.ts';
import { ImageUpload } from '../components/ImageUpload.jsx';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock.js';

export function FeedbackModal({ isOpen, onClose }) {
  const [step, setStep] = useState('select'); // 'select', 'form', 'success'
  const [feedbackType, setFeedbackType] = useState(null);
  const [message, setMessage] = useState('');
  const [images, setImages] = useState([null, null, null, null, null]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Bloquear scroll del body cuando el modal está abierto
  useBodyScrollLock(isOpen);

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setStep('select');
      setFeedbackType(null);
      setMessage('');
      setImages([null, null, null, null, null]);
    }, 300);
  };

  const handleSelectType = (type) => {
    setFeedbackType(type);
    setStep('form');
  };

  const handleImageChange = (index, url) => {
    const newImages = [...images];
    newImages[index] = url;
    setImages(newImages);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (message.trim().length < 30) {
      toast.error('El mensaje debe tener al menos 30 caracteres');
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        feedback_type: feedbackType,
        message: message.trim(),
        page_url: window.location.href,
      };

      // Agregar imágenes no vacías
      images.forEach((img, index) => {
        if (img) {
          payload[`image_${index + 1}`] = img;
        }
      });

      await api.post('/api/merchant/feedback', payload);
      
      setStep('success');
      
      // Cerrar automáticamente después de 3 segundos
      setTimeout(() => {
        handleClose();
      }, 3000);
      
    } catch (error) {
      console.error('Error enviando feedback:', error);
      toast.error('Error al enviar el feedback. Intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-200"
      onClick={handleClose}
    >
      <div
        className="bg-[#0F0F0F] border border-white/10 rounded-[2rem] p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl relative animate-in zoom-in-95 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Luz decorativa */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-[80px] pointer-events-none" />

        {/* Botón cerrar */}
        <button
          onClick={handleClose}
          className="absolute top-6 right-6 p-2 rounded-full bg-white/5 hover:bg-white/10 text-white/40 hover:text-white transition-colors z-10"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="relative z-10">
          {/* PASO 1: Seleccionar Tipo */}
          {step === 'select' && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-white mb-2">
                  ¿Cómo podemos ayudarte?
                </h2>
                <p className="text-white/40 text-sm">
                  Selecciona el tipo de feedback que deseas enviar
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Opción: Problema */}
                <button
                  onClick={() => handleSelectType('problem')}
                  className="group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-red-500/10 to-red-900/10 p-8 hover:border-red-500/30 hover:from-red-500/20 hover:to-red-900/20 transition-all duration-300"
                >
                  <div className="flex flex-col items-center gap-4 text-center">
                    <div className="p-4 rounded-2xl bg-red-500/20 text-red-400 group-hover:scale-110 transition-transform">
                      <AlertCircle className="h-8 w-8" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white mb-1">
                        Reportar Problema
                      </h3>
                      <p className="text-xs text-white/50">
                        Algo no funciona correctamente
                      </p>
                    </div>
                  </div>
                </button>

                {/* Opción: Sugerencia */}
                <button
                  onClick={() => handleSelectType('suggestion')}
                  className="group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-yellow-500/10 to-yellow-900/10 p-8 hover:border-yellow-500/30 hover:from-yellow-500/20 hover:to-yellow-900/20 transition-all duration-300"
                >
                  <div className="flex flex-col items-center gap-4 text-center">
                    <div className="p-4 rounded-2xl bg-yellow-500/20 text-yellow-400 group-hover:scale-110 transition-transform">
                      <Lightbulb className="h-8 w-8" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white mb-1">
                        Enviar Sugerencia
                      </h3>
                      <p className="text-xs text-white/50">
                        Comparte una idea de mejora
                      </p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* PASO 2: Formulario */}
          {step === 'form' && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="text-center mb-6">
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${
                  feedbackType === 'problem' 
                    ? 'bg-red-500/20 text-red-400 border border-red-500/30' 
                    : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                }`}>
                  {feedbackType === 'problem' ? (
                    <><AlertCircle className="h-4 w-4" /> Reportar Problema</>
                  ) : (
                    <><Lightbulb className="h-4 w-4" /> Enviar Sugerencia</>
                  )}
                </div>
              </div>

              {/* Mensaje */}
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-white/40 mb-2">
                  Descripción *
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={
                    feedbackType === 'problem'
                      ? 'Describe el problema que encontraste...'
                      : 'Cuéntanos tu idea de mejora...'
                  }
                  required
                  rows={6}
                  className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-white placeholder-white/20 focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/20 focus:outline-none transition-all resize-none"
                />
                <p className="text-xs text-white/30 mt-1">
                  Mínimo 30 caracteres ({message.length}/30)
                </p>
              </div>

              {/* Imágenes */}
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-white/40 mb-3">
                  Capturas de Pantalla (Opcional)
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {images.map((img, index) => (
                    <div key={index}>
                      <ImageUpload
                        value={img}
                        onChange={(url) => handleImageChange(index, url)}
                        disabled={isSubmitting}
                        height="h-32"
                        rounded="rounded-xl"
                        placeholderText={`Imagen ${index + 1}`}
                      />
                    </div>
                  ))}
                </div>
                <p className="text-xs text-white/30 mt-2">
                  Puedes subir hasta 5 imágenes para ilustrar mejor tu feedback
                </p>
              </div>

              {/* Botones */}
              <div className="flex items-center gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setStep('select')}
                  disabled={isSubmitting}
                  className="flex-1 px-6 py-3 rounded-xl border border-white/10 text-white/60 text-xs font-bold uppercase tracking-widest hover:bg-white/5 hover:text-white transition-all disabled:opacity-50"
                >
                  Atrás
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || message.trim().length < 30}
                  className="flex-1 px-6 py-3 rounded-xl bg-white text-black text-xs font-bold uppercase tracking-widest hover:bg-gray-200 shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4" />
                      Enviar Feedback
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* PASO 3: Éxito */}
          {step === 'success' && (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-500/20 text-green-400 mb-6 animate-in zoom-in duration-500">
                <Check className="h-10 w-10" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">
                ¡Feedback Enviado!
              </h2>
              <p className="text-white/60 mb-6">
                Gracias por ayudarnos a mejorar TiendasYA.
                <br />
                Revisaremos tu {feedbackType === 'problem' ? 'reporte' : 'sugerencia'} pronto.
              </p>
              <button
                onClick={handleClose}
                className="px-6 py-3 rounded-xl bg-white text-black text-xs font-bold uppercase tracking-widest hover:bg-gray-200 transition-all"
              >
                Cerrar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
